import { Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { Taxonomy, IdentifiedGap } from "@/lib/learnerHub/types";
import { flattenTaxonomyUnits } from "@/lib/learnerHub/utils";

interface Props {
  step: number;
  taxonomy: Taxonomy;
  learnerCount: number;
  identifiedGaps: IdentifiedGap[];
  onNext: () => void;
  onBack: () => void;
  onExit: () => void;
}

const STEPS = [
  { n: 1, label: "Build taxonomy" },
  { n: 2, label: "Import workforce" },
  { n: 3, label: "Assess gaps" },
  { n: 4, label: "Generate courses" },
];

function StepDot({ n, state }: { n: number; state: "done" | "current" | "future" }) {
  const base = "h-[26px] w-[26px] rounded-full border flex items-center justify-center text-xs font-medium shrink-0";
  if (state === "done")
    return (
      <div className={`${base} bg-green-600 text-white border-transparent`}>
        <Check className="h-3 w-3" />
      </div>
    );
  if (state === "current")
    return <div className={`${base} bg-foreground text-background border-transparent`}>{n}</div>;
  return <div className={`${base} bg-muted text-muted-foreground border-border`}>{n}</div>;
}

function DemoContext({ step, taxonomy, learnerCount, identifiedGaps }: Omit<Props, "onNext" | "onBack" | "onExit">) {
  if (step === 1) {
    return <span>Define the skills your workforce needs to hold.</span>;
  }
  if (step === 2) {
    const unitCount = flattenTaxonomyUnits(taxonomy).length;
    return (
      <span>
        Using <strong>{taxonomy.name}</strong> — {unitCount} skill unit{unitCount !== 1 ? "s" : ""}.
        Import qualification records from each site and reconcile them to this framework.
      </span>
    );
  }
  if (step === 3) {
    const sites = learnerCount > 0 ? Math.max(1, Math.round(learnerCount / 4)) : 0;
    return (
      <span>
        <strong>{learnerCount} employee{learnerCount !== 1 ? "s" : ""}</strong>
        {" "}from{" "}
        <strong>{sites} site{sites !== 1 ? "s" : ""}</strong> unified.
        Select a role to see who has what.
      </span>
    );
  }
  const n = identifiedGaps.length;
  if (n === 0) {
    return <span>Generate targeted training courses for your workforce.</span>;
  }
  return (
    <span>
      <strong>{n} training gap{n !== 1 ? "s" : ""}</strong> identified.
      Generate a targeted course for each one.
    </span>
  );
}

export default function DemoRail({ step, taxonomy, learnerCount, identifiedGaps, onNext, onBack, onExit }: Props) {
  const isLast = step === 4;
  const canGoNext = step === 1
    ? true // CCM taxonomy is always present
    : step === 2
    ? learnerCount > 0
    : true;

  const nextLabel = isLast ? "Finish walkthrough" : `Next: ${STEPS[step].label} →`;

  return (
    <div className="border-b bg-muted/40 px-6 py-3">
      {/* Step indicators */}
      <div className="flex items-center mb-2">
        {STEPS.map((s, i) => {
          const state = s.n < step ? "done" : s.n === step ? "current" : "future";
          return (
            <div key={s.n} className="flex items-center">
              <div className="flex items-center gap-2">
                <StepDot n={s.n} state={state} />
                <span
                  className={`text-[13px] ${
                    state === "current"
                      ? "font-medium text-foreground"
                      : "text-muted-foreground"
                  }`}
                >
                  {s.label}
                </span>
              </div>
              {i < STEPS.length - 1 && (
                <div className="mx-3 h-px w-8 min-w-[16px] flex-1 bg-border" />
              )}
            </div>
          );
        })}
      </div>

      {/* Context + navigation */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <p className="text-[13px] text-muted-foreground leading-relaxed">
          <DemoContext
            step={step}
            taxonomy={taxonomy}
            learnerCount={learnerCount}
            identifiedGaps={identifiedGaps}
          />
        </p>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={onExit}
            className="text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            Exit demo
          </button>
          {step > 1 && (
            <Button variant="outline" size="sm" onClick={onBack}>
              ← Back
            </Button>
          )}
          <Button size="sm" onClick={isLast ? onExit : onNext} disabled={!canGoNext}>
            {nextLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
